/**
 * Fila de prioridade dos modais que disputam a abertura do painel.
 *
 * O PROBLEMA: três coisas querem aparecer assim que a Visão Geral carrega —
 * o aviso de loja fechada ("ESTOU PRONTO"), o popup de novidades e o tutorial
 * guiado. Cada um foi construído sem saber dos outros, então empilhavam: o
 * balão do tutorial ficava por cima do popup de novidades, e o overlay de um
 * roubava os cliques do outro. Resultado: tela travada, nada respondia.
 *
 * A SOLUÇÃO: uma prioridade única e explícita. Cada modal declara que está
 * aberto; quem tem prioridade MENOR espera todo mundo acima fechar.
 *
 *   1. loja-fechada  — operacional e urgente: a loja não abre até alguém
 *                      clicar. Nada pode ficar na frente disso.
 *   2. novidades     — informativo, 1× por sessão.
 *   3. tour          — onboarding, pode esperar; se não couber hoje, tenta na
 *                      próxima visita (não marca como visto).
 *
 * POR QUE data-attribute no body, e não Context: os três vivem em pontos
 * distantes da árvore (um em `components/`, outro em `features/tour/`, aberto
 * dentro da página) e um deles monta antes do provider. Um atributo no body é
 * barato, não exige refator de árvore e é inspecionável no DevTools.
 */

export const PRIORIDADE = ["loja-fechada", "novidades", "tour"];

const attr = (nome) => `modal${nome.replace(/(^|-)(\w)/g, (_, __, c) => c.toUpperCase())}Open`;

/** Marca este modal como aberto. Devolve a função de limpeza. */
export function marcarAberto(nome) {
  document.body.dataset[attr(nome)] = "true";
  return () => {
    delete document.body.dataset[attr(nome)];
  };
}

/**
 * Existe algum modal de prioridade MAIOR aberto agora?
 *
 * O `storePromptOpen` legado continua sendo lido: o OpenStorePrompt já usava
 * esse nome antes desta fila existir, e o ReleaseNotesModal já dependia dele.
 * Trocar o nome nos dois de uma vez seria mudança maior sem ganho.
 */
export function bloqueadoPor(nome) {
  const eu = PRIORIDADE.indexOf(nome);
  if (eu < 0) return null;
  for (let i = 0; i < eu; i += 1) {
    const acima = PRIORIDADE[i];
    const legado = acima === "loja-fechada" && document.body.dataset.storePromptOpen === "true";
    if (legado || document.body.dataset[attr(acima)] === "true") return acima;
  }
  return null;
}

/**
 * Espera a vez de `nome` e chama `aoLiberar`. Devolve a função de cancelamento.
 *
 * Reconsulta a cada 600ms em vez de usar evento: os modais acima abrem e fecham
 * por caminhos diferentes (um deles fora do React), e um polling curto é mais
 * robusto que tentar instrumentar cada saída. O custo é uma leitura de
 * data-attribute por meio segundo, só enquanto há espera.
 */
export function aguardarVez(nome, aoLiberar, { atrasoInicial = 800, intervalo = 600 } = {}) {
  let cancelado = false;
  let timer = null;
  const tentar = () => {
    if (cancelado) return;
    if (bloqueadoPor(nome)) {
      timer = setTimeout(tentar, intervalo);
      return;
    }
    aoLiberar();
  };
  timer = setTimeout(tentar, atrasoInicial);
  return () => {
    cancelado = true;
    if (timer) clearTimeout(timer);
  };
}
